import * as _ from "lodash-es";
import type {
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_SortingState,
  MRT_Updater
} from "mantine-react-table-open";
import { useState } from "react";
import { DEFAULT_MRT_PAGE_SIZE } from "./getDefaultMRTOptions";

interface UseResetPageOnFilterOrSortChangeOptions {
  initialGlobalFilter?: string;
  initialColumnFilters?: MRT_ColumnFiltersState;
  initialSorting?: MRT_SortingState;
}

function applyUpdater<T>(updater: MRT_Updater<T>, old: T): T {
  return typeof updater === "function"
    ? (updater as (old: T) => T)(old)
    : updater;
}

/**
 * Owns global filter, column filter, sorting and pagination state for a table
 * with `autoResetPageIndex: false`, and returns to page 1 whenever the filter
 * or sorting changes. Pass `state` into the table's `state` and the `on*Change`
 * handlers into its options.
 */
export function useResetPageOnFilterOrSortChange({
  initialGlobalFilter,
  initialColumnFilters = [],
  initialSorting = []
}: UseResetPageOnFilterOrSortChangeOptions = {}) {
  const [globalFilter, setGlobalFilter] = useState<string | undefined>(
    initialGlobalFilter
  );
  const [columnFilters, setColumnFilters] =
    useState<MRT_ColumnFiltersState>(initialColumnFilters);
  const [sorting, setSorting] = useState<MRT_SortingState>(initialSorting);
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: DEFAULT_MRT_PAGE_SIZE
  });

  function resetPageIndex() {
    setPagination((old) => ({ ...old, pageIndex: 0 }));
  }

  function onGlobalFilterChange(updater: MRT_Updater<string | undefined>) {
    const next = applyUpdater(updater, globalFilter);
    if (next === globalFilter) {
      return;
    }
    setGlobalFilter(next);
    resetPageIndex();
  }

  function onColumnFiltersChange(updater: MRT_Updater<MRT_ColumnFiltersState>) {
    const next = applyUpdater(updater, columnFilters);
    if (_.isEqual(next, columnFilters)) {
      return;
    }
    setColumnFilters(next);
    resetPageIndex();
  }

  function onSortingChange(updater: MRT_Updater<MRT_SortingState>) {
    const next = applyUpdater(updater, sorting);
    if (_.isEqual(next, sorting)) {
      return;
    }
    setSorting(next);
    resetPageIndex();
  }

  function onPaginationChange(updater: MRT_Updater<MRT_PaginationState>) {
    setPagination((old) => applyUpdater(updater, old));
  }

  return {
    state: { globalFilter, columnFilters, sorting, pagination },
    onGlobalFilterChange,
    onColumnFiltersChange,
    onSortingChange,
    onPaginationChange,
    resetPageIndex
  };
}
